// ---------------------------------------------------------------------------
// EM-023 Phase 5 — Per-provider Inbox Optimization CQRS Event Catalog
// ---------------------------------------------------------------------------
// Story EM-023: "Per-provider inbox optimization detects Gmail/Outlook/Yahoo
// rendering/deliverability issues and suggests fixes."
//
// Phase 1 (LANDED) shipped the pure provider classifier
// `packages/email-marketing/src/engagement/em-023-provider-inbox-classifier.ts`
// with closed `INBOX_PROVIDERS` (5), `INBOX_DELIVERABILITY_SIGNALS` (16) and
// `INBOX_FIX_RECOMMENDATIONS` (12) catalogs and the canonical event constant
// `EM_023_PROVIDER_SIGNAL_DETECTED_EVENT =
// "email.provider_signal_detected@v1"`.
//
// This file is the Phase 5 narrow follow-up: it re-exposes those closed
// vocabularies at the database tier so emitters, materializers, DLQ
// consumers and audit-runner read them without taking a runtime dependency
// on `@ease/email-marketing`. Per Governance Rule #7 ("Event Catalog"):
// every event type must be catalogued BEFORE Phase 3 runtime wiring so
// emitters import the constants instead of hand-rolling drift-prone strings.
//
// What this file is NOT:
//  - It does not emit events — that's Phase 3 (worker integration).
//  - It does not mount routes or open a worker — Phase 3+ scope.
//  - It does not change the Phase 1 classifier contract (additive only).
//
// What it IS:
//  - The single source of truth for `email.provider_signal_*` event strings.
//  - Typed payload contracts so emitters and DLQ consumers cannot drift.
//  - Runtime narrowing guards so DLQ/audit-runner reject unknown event types.
// ---------------------------------------------------------------------------

/**
 * Closed catalog of EM-023 CQRS event types. Versioned with the `@v1` suffix
 * per platform convention so a future shape change ships a `@v2` alongside
 * (never mutate in place).
 *
 * Mirrors `EM_023_PROVIDER_SIGNAL_DETECTED_EVENT` from Phase 1 verbatim,
 * re-exposed here so the database tier does not invert dependency direction
 * onto `@ease/email-marketing`.
 *
 * One business fact per event (Governance Rule #6):
 *  - `signal_detected` — classifier flagged one or more provider-specific
 *    deliverability signals on an outbound email draft.
 */
export const EM_023_PROVIDER_INBOX_EVENT_TYPES = [
  "email.provider_signal_detected@v1",
] as const;

export type Em023ProviderInboxEventType =
  (typeof EM_023_PROVIDER_INBOX_EVENT_TYPES)[number];

/**
 * Closed catalog of inbox providers — mirrors Phase 1 `INBOX_PROVIDERS`
 * verbatim. Used by the `signal_detected@v1` payload to tag the event with
 * the provider whose rules triggered.
 */
export const EM_023_INBOX_PROVIDER_VOCAB = [
  "gmail",
  "outlook",
  "yahoo",
  "apple_icloud",
  "generic",
] as const;

export type Em023InboxProviderVocab =
  (typeof EM_023_INBOX_PROVIDER_VOCAB)[number];

/**
 * Closed catalog of deliverability signals — mirrors Phase 1
 * `INBOX_DELIVERABILITY_SIGNALS` verbatim. Carried in `signal_detected@v1`
 * so downstream consumers can fan out per-signal remediation.
 */
export const EM_023_DELIVERABILITY_SIGNAL_VOCAB = [
  "gmail_clipping_risk",
  "outlook_image_blocked_by_default",
  "yahoo_aggressive_spam_routing",
  "apple_mpp_prefetch_open_inflation",
  "gmail_promo_tab_routing_risk",
  "dark_mode_render_break",
  "dmarc_quarantine_at_yahoo",
  "missing_list_unsubscribe_header",
  "high_spam_complaint_risk",
  "low_text_to_image_ratio",
  "excessive_links",
  "all_caps_subject",
  "free_url_shortener",
  "missing_alt_text_on_images",
  "rgb_color_outlook_break",
  "background_image_outlook_unsupported",
] as const;

export type Em023DeliverabilitySignalVocab =
  (typeof EM_023_DELIVERABILITY_SIGNAL_VOCAB)[number];

/**
 * Closed catalog of fix recommendations — mirrors Phase 1
 * `INBOX_FIX_RECOMMENDATIONS` verbatim. Phase 4 UI renders these as
 * actionable buttons.
 */
export const EM_023_FIX_RECOMMENDATION_VOCAB = [
  "reduce_html_size_under_102kb",
  "add_list_unsubscribe_one_click_header",
  "authenticate_dmarc_aligned",
  "increase_text_to_image_ratio",
  "remove_url_shorteners_use_branded",
  "add_alt_text_to_all_images",
  "test_dark_mode_render",
  "replace_rgb_with_hex_for_outlook",
  "use_vml_fallback_for_outlook_backgrounds",
  "shorten_subject_remove_all_caps",
  "reduce_link_count_under_20",
  "monitor_apple_mpp_inflated_opens",
] as const;

export type Em023FixRecommendationVocab =
  (typeof EM_023_FIX_RECOMMENDATION_VOCAB)[number];

/**
 * Closed risk-severity ladder — mirrors Phase 1 `INBOX_RISK_SEVERITIES`.
 */
export const EM_023_RISK_SEVERITY_VOCAB = [
  "low",
  "medium",
  "high",
  "critical",
] as const;

export type Em023RiskSeverityVocab =
  (typeof EM_023_RISK_SEVERITY_VOCAB)[number];

/* -------------------------------------------------------------------------- */
/* Runtime narrowing guards                                                    */
/* -------------------------------------------------------------------------- */

export function isEm023ProviderInboxEventType(
  value: unknown,
): value is Em023ProviderInboxEventType {
  return (
    typeof value === "string" &&
    (EM_023_PROVIDER_INBOX_EVENT_TYPES as readonly string[]).includes(value)
  );
}

export function isEm023InboxProviderVocab(
  value: unknown,
): value is Em023InboxProviderVocab {
  return (
    typeof value === "string" &&
    (EM_023_INBOX_PROVIDER_VOCAB as readonly string[]).includes(value)
  );
}

export function isEm023DeliverabilitySignalVocab(
  value: unknown,
): value is Em023DeliverabilitySignalVocab {
  return (
    typeof value === "string" &&
    (EM_023_DELIVERABILITY_SIGNAL_VOCAB as readonly string[]).includes(value)
  );
}

export function isEm023FixRecommendationVocab(
  value: unknown,
): value is Em023FixRecommendationVocab {
  return (
    typeof value === "string" &&
    (EM_023_FIX_RECOMMENDATION_VOCAB as readonly string[]).includes(value)
  );
}

export function isEm023RiskSeverityVocab(
  value: unknown,
): value is Em023RiskSeverityVocab {
  return (
    typeof value === "string" &&
    (EM_023_RISK_SEVERITY_VOCAB as readonly string[]).includes(value)
  );
}

/* -------------------------------------------------------------------------- */
/* Per-event payload contracts                                                 */
/* -------------------------------------------------------------------------- */

export interface Em023ProviderInboxBaseEventPayload {
  /** Tenant scope for fan-out + isolation. */
  tenantId: string;
  /** FK into the email draft being classified. */
  draftId: string;
  /** Provider whose rules fired. */
  provider: Em023InboxProviderVocab;
}

export interface Em023ProviderSignalDetectedPayload
  extends Em023ProviderInboxBaseEventPayload {
  /**
   * Non-empty list of signals the classifier flagged. Each value MUST
   * narrow to `EM_023_DELIVERABILITY_SIGNAL_VOCAB` — DLQ consumers reject
   * the event otherwise.
   */
  signals: readonly Em023DeliverabilitySignalVocab[];
  /**
   * Recommended fixes the UI should surface. Each value MUST narrow to
   * `EM_023_FIX_RECOMMENDATION_VOCAB`.
   */
  recommendations: readonly Em023FixRecommendationVocab[];
  /** Risk severity assigned by the classifier. */
  severity: Em023RiskSeverityVocab;
  /** ISO-8601 — when the signal was detected. */
  detectedAt: string;
}

/**
 * Discriminated union mapping each event type to its payload. Consumers
 * (Phase 3 emitter, materializer, DLQ, audit-runner) `switch (event.type)`
 * and pull the right payload without any-casting.
 */
export type Em023ProviderInboxEvent = {
  type: "email.provider_signal_detected@v1";
  payload: Em023ProviderSignalDetectedPayload;
};
